/**
 * Meeting Brief Module - Type Definitions
 * Light QA Contract Compliant
 */

// ============================================================
// Enums
// ============================================================
export type MeetingStatus = 'upcoming' | 'in-progress' | 'paused' | 'completed' | 'cancelled';
export type AttendeeRole = 'owner' | 'participant' | 'observer';
export type AttendanceStatus = 'present' | 'absent' | 'pending' | 'excused';
export type ActionItemPriority = 'high' | 'medium' | 'low';
export type ActionItemStatus = 'pending' | 'completed' | 'cancelled';

// ============================================================
// Labels & Colors (UI Constants)
// ============================================================
export const MEETING_STATUS_LABELS: Record<MeetingStatus, string> = {
  'upcoming': 'Sắp diễn ra',
  'in-progress': 'Đang diễn ra',
  'paused': 'Tạm dừng',
  'completed': 'Hoàn thành',
  'cancelled': 'Đã hủy',
};

export const MEETING_STATUS_COLORS: Record<MeetingStatus, string> = {
  'upcoming': 'bg-blue-100 text-blue-800',
  'in-progress': 'bg-yellow-100 text-yellow-800',
  'paused': 'bg-orange-100 text-orange-800',
  'completed': 'bg-green-100 text-green-800',
  'cancelled': 'bg-red-100 text-red-800',
};

export const ATTENDEE_ROLE_LABELS: Record<AttendeeRole, string> = {
  'owner': 'Chủ trì',
  'participant': 'Tham dự',
  'observer': 'Quan sát',
};

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  'present': 'Có mặt',
  'absent': 'Vắng mặt',
  'pending': 'Chưa xác nhận',
  'excused': 'Có phép',
};

export const ATTENDANCE_STATUS_COLORS: Record<AttendanceStatus, string> = {
  'present': 'bg-green-100 text-green-800',
  'absent': 'bg-red-100 text-red-800',
  'pending': 'bg-gray-100 text-gray-800',
  'excused': 'bg-yellow-100 text-yellow-800',
};

export const ACTION_PRIORITY_LABELS: Record<ActionItemPriority, string> = {
  'high': 'Cao',
  'medium': 'Trung bình',
  'low': 'Thấp',
};

export const ACTION_PRIORITY_COLORS: Record<ActionItemPriority, string> = {
  'high': 'bg-red-100 text-red-800',
  'medium': 'bg-yellow-100 text-yellow-800',
  'low': 'bg-blue-100 text-blue-800',
};

export const ACTION_STATUS_LABELS: Record<ActionItemStatus, string> = {
  'pending': 'Đang chờ',
  'completed': 'Hoàn thành',
  'cancelled': 'Đã hủy',
};

// ============================================================
// Interfaces
// ============================================================
export interface Meeting {
  id: string;
  project_id: string;
  title: string;
  description?: string | null;
  meeting_date: string;
  start_time: string;
  end_time?: string | null;
  location?: string | null;
  meeting_link?: string | null;
  status: MeetingStatus;
  notes?: string | null;
  summary?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  // Time tracking fields
  actual_start_time?: string | null;
  actual_end_time?: string | null;
  total_pause_duration?: string | null;
  pause_started_at?: string | null;
}

export interface MeetingObjective {
  id: string;
  meeting_id: string;
  text: string;
  is_completed: boolean;
  sort_order: number;
  created_at: string;
}

export interface MeetingAgendaItem {
  id: string;
  meeting_id: string;
  title: string;
  duration_minutes?: number | null;
  owner_id?: string | null;
  notes?: string | null;
  sort_order: number;
  created_at: string;
  // Joined data
  owner_name?: string;
}

export interface MeetingAttendee {
  id: string;
  meeting_id: string;
  user_id?: string | null;
  name?: string | null;
  email?: string | null;
  role: AttendeeRole;
  attendance: AttendanceStatus;
  created_at: string;
  // Joined data
  user_avatar?: string;
  user_full_name?: string;
}

export interface MeetingAttachment {
  id: string;
  meeting_id: string;
  file_name: string;
  file_url: string;
  file_type?: string | null;
  file_size?: number | null;
  uploaded_by?: string | null;
  created_at: string;
}

export interface MeetingDecision {
  id: string;
  meeting_id: string;
  text: string;
  created_at: string;
}

export interface MeetingActionItem {
  id: string;
  meeting_id: string;
  title: string;
  assignee_id?: string | null;
  assignee_name?: string | null;
  deadline?: string | null;
  priority: ActionItemPriority;
  status: ActionItemStatus;
  linked_task_id?: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  assignee_avatar?: string;
}

// ============================================================
// Extended Meeting with related data
// ============================================================
export interface MeetingWithDetails extends Meeting {
  objectives: MeetingObjective[];
  agenda_items: MeetingAgendaItem[];
  attendees: MeetingAttendee[];
  attachments: MeetingAttachment[];
  decisions: MeetingDecision[];
  action_items: MeetingActionItem[];
  // Computed stats
  objectives_completed?: number;
  attendees_present?: number;
  action_items_pending?: number;
}

// ============================================================
// Form Types
// ============================================================
export interface MeetingFormData {
  title: string;
  description?: string;
  meeting_date: string;
  start_time: string;
  end_time?: string;
  location?: string;
  meeting_link?: string;
  status?: MeetingStatus; // Optional - only used in edit mode
}

export interface ObjectiveFormData {
  text: string;
}

export interface AgendaItemFormData {
  title: string;
  duration_minutes?: number;
  owner_id?: string;
}

export interface AttendeeFormData {
  user_id?: string;
  name?: string;
  email?: string;
  role: AttendeeRole;
}

export interface DecisionFormData {
  text: string;
}

export interface ActionItemFormData {
  title: string;
  assignee_id?: string;
  assignee_name?: string;
  deadline?: string;
  priority: ActionItemPriority;
}

// ============================================================
// Filter Types
// ============================================================
export interface MeetingFilters {
  status?: MeetingStatus | 'all';
  startDate?: string;
  endDate?: string;
  search?: string;
}

// ============================================================
// Favorite Types
// ============================================================
export interface MeetingFavorite {
  id: string;
  user_id: string;
  meeting_id: string;
  created_at: string;
}
